import { Bot, ChevronDown, ChevronRight, Power, Search, TrendingUp, Users, X } from 'lucide-react';
import { useState } from 'react';
import { Badge } from '../components/Badge';
import type { PipelineOrganization, PipelineStage, StageAgentConfig } from '../hooks/useAdminData';
import { usePipelineAgents, useUpdateStageAgents } from '../hooks/useAdminData';
import { navigate } from '../lib/navigation';

const ALL_STAGE_AGENTS = [
  'stage_prospect_intake',
  'stage_first_contact',
  'stage_qualification_deep',
  'stage_proposal_prep',
  'stage_won_onboarding',
  'stage_lost_analysis',
] as const;

const AGENT_LABELS: Record<string, string> = {
  stage_prospect_intake: 'Prospect Intake',
  stage_first_contact: 'First Contact',
  stage_qualification_deep: 'Deep Qualification',
  stage_proposal_prep: 'Proposal Prep',
  stage_won_onboarding: 'Won Onboarding',
  stage_lost_analysis: 'Lost Analysis',
  lead_qualification: 'Lead Qualification',
  lead_enrichment: 'Lead Enrichment',
  follow_up_scheduler: 'Follow-up Scheduler',
  sentiment_analysis: 'Sentiment Analysis',
};

const AGENT_DESCRIPTIONS: Record<string, string> = {
  stage_prospect_intake: 'Evaluates new prospect data completeness and pipeline readiness',
  stage_first_contact: 'Crafts personalized outreach based on prospect profile',
  stage_qualification_deep: 'Deep assessment of fit, budget, and decision-making authority',
  stage_proposal_prep: 'Generates proposal talking points and competitive positioning',
  stage_won_onboarding: 'Creates onboarding checklists and success milestones',
  stage_lost_analysis: 'Analyzes loss reasons and recommends win-back strategies',
  lead_qualification: 'Scores and qualifies leads based on engagement signals',
  lead_enrichment: 'Enriches lead profiles with additional data points',
  follow_up_scheduler: 'Optimizes follow-up timing and messaging cadence',
  sentiment_analysis: 'Analyzes communication tone and engagement sentiment',
};

function formatAgentType(agentType: string) {
  return AGENT_LABELS[agentType] ?? agentType.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

function ToggleSwitch({
  enabled,
  onToggle,
  disabled,
}: {
  enabled: boolean;
  onToggle: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
        enabled ? 'bg-primary' : 'bg-border'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <span
        className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform ${
          enabled ? 'translate-x-[18px]' : 'translate-x-[3px]'
        }`}
      />
    </button>
  );
}

interface AgentCardProps {
  agent: StageAgentConfig;
  orgId: string;
  onToggle: (agentType: string, enabled: boolean) => void;
  onEdit: (agent: StageAgentConfig) => void;
  saving: boolean;
}

function AgentCard({ agent, orgId, onToggle, onEdit, saving }: AgentCardProps) {
  return (
    <div
      className={`flex items-center gap-3 px-3 py-2 rounded-lg border transition-colors ${
        agent.isEnabled
          ? 'border-primary/30 bg-primary-muted/50'
          : 'border-border bg-surface-hover/50 opacity-60'
      }`}
    >
      <Bot size={16} className={agent.isEnabled ? 'text-primary' : 'text-text-muted'} />
      <button
        type="button"
        onClick={() => navigate(`/pipeline-agents/${orgId}/${agent.agentType}`)}
        className="flex-1 min-w-0 text-left hover:opacity-80 transition-opacity"
      >
        <p className="text-xs font-medium text-text truncate hover:text-primary transition-colors">{formatAgentType(agent.agentType)}</p>
        {agent.customPrompt && (
          <p className="text-[10px] text-primary truncate">Custom prompt</p>
        )}
      </button>
      <div className="flex items-center gap-2">
        <button
          onClick={() => onEdit(agent)}
          className="text-[10px] text-text-muted hover:text-text transition-colors"
        >
          Edit
        </button>
        <ToggleSwitch
          enabled={agent.isEnabled}
          onToggle={() => onToggle(agent.agentType, !agent.isEnabled)}
          disabled={saving}
        />
      </div>
    </div>
  );
}

interface StageColumnProps {
  stage: PipelineStage;
  orgId: string;
  onToggleAgent: (stageId: string, agentType: string, enabled: boolean) => void;
  onEditAgent: (stageId: string, agent: StageAgentConfig) => void;
  onAddAgent: (stageId: string) => void;
  saving: boolean;
}

function StageColumn({ stage, orgId, onToggleAgent, onEditAgent, onAddAgent, saving }: StageColumnProps) {
  const assignedTypes = new Set(stage.agents.map((a) => a.agentType));
  const hasUnassigned = ALL_STAGE_AGENTS.some((t) => !assignedTypes.has(t));

  return (
    <div className="flex-shrink-0 w-64">
      <div className="bg-surface rounded-xl border border-border overflow-hidden">
        <div className="px-4 py-3 border-b border-border">
          <div className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-full flex-shrink-0"
              style={{ backgroundColor: stage.color ?? '#6b7280' }}
            />
            <h3 className="text-sm font-medium text-text truncate">{stage.name}</h3>
          </div>
          <div className="flex items-center gap-3 mt-1.5">
            <span className="text-xs text-text-muted flex items-center gap-1">
              <Users size={12} />
              {stage.leadCount} leads
            </span>
            <span className="text-xs text-text-muted flex items-center gap-1">
              <Bot size={12} />
              {stage.agents.filter((a) => a.isEnabled).length} active
            </span>
          </div>
        </div>

        <div className="p-3 space-y-2">
          {stage.agents.length === 0 ? (
            <p className="text-xs text-text-muted text-center py-3">No agents assigned</p>
          ) : (
            stage.agents.map((agent) => (
              <AgentCard
                key={agent.id}
                agent={agent}
                orgId={orgId}
                onToggle={(agentType, enabled) => onToggleAgent(stage.id, agentType, enabled)}
                onEdit={(a) => onEditAgent(stage.id, a)}
                saving={saving}
              />
            ))
          )}

          {hasUnassigned && (
            <button
              onClick={() => onAddAgent(stage.id)}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-dashed border-border text-xs text-text-muted hover:text-text hover:border-primary/50 transition-colors"
            >
              <Bot size={14} />
              Add Agent
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

interface AddAgentModalProps {
  stage: PipelineStage;
  onAdd: (agentType: string) => void;
  onClose: () => void;
}

function AddAgentModal({ stage, onAdd, onClose }: AddAgentModalProps) {
  const existingTypes = new Set(stage.agents.map((a) => a.agentType));
  const available = [...ALL_STAGE_AGENTS, 'lead_qualification', 'lead_enrichment', 'follow_up_scheduler', 'sentiment_analysis'].filter(
    (t) => !existingTypes.has(t),
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-surface border border-border rounded-xl shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="text-base font-semibold text-text">
            Add Agent to {stage.name}
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md text-text-muted hover:text-text hover:bg-surface-hover transition-colors"
          >
            <X size={18} />
          </button>
        </div>
        <div className="p-4 space-y-2 max-h-80 overflow-y-auto">
          {available.length === 0 ? (
            <p className="text-sm text-text-muted text-center py-4">All agents are already assigned</p>
          ) : (
            available.map((agentType) => (
              <button
                key={agentType}
                onClick={() => {
                  onAdd(agentType);
                  onClose();
                }}
                className="w-full flex items-start gap-3 px-3 py-3 rounded-lg border border-border hover:border-primary/50 hover:bg-primary-muted/30 transition-colors text-left"
              >
                <Bot size={18} className="text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-text">{formatAgentType(agentType)}</p>
                  <p className="text-xs text-text-muted mt-0.5">
                    {AGENT_DESCRIPTIONS[agentType] ?? ''}
                  </p>
                </div>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

interface EditAgentModalProps {
  agent: StageAgentConfig;
  stageName: string;
  onSave: (customPrompt: string | null) => void;
  onClose: () => void;
  saving: boolean;
}

function EditAgentModal({ agent, stageName, onSave, onClose, saving }: EditAgentModalProps) {
  const [customPrompt, setCustomPrompt] = useState(agent.customPrompt ?? '');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-surface border border-border rounded-xl shadow-xl w-full max-w-lg mx-4">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div>
            <h2 className="text-base font-semibold text-text">
              {formatAgentType(agent.agentType)}
            </h2>
            <p className="text-xs text-text-muted mt-0.5">Stage: {stageName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md text-text-muted hover:text-text hover:bg-surface-hover transition-colors"
          >
            <X size={18} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-sm font-medium text-text-secondary">
                Custom Prompt Override
              </label>
              <span className="text-xs text-text-muted">{customPrompt.length} chars</span>
            </div>
            <textarea
              value={customPrompt}
              onChange={(e) => setCustomPrompt(e.target.value)}
              rows={8}
              className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-text font-mono resize-y focus:outline-none focus:border-primary leading-relaxed"
              placeholder="Leave empty to use the global default prompt..."
            />
            <p className="text-xs text-text-muted mt-1">
              If empty, the global default prompt for this agent type will be used.
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t border-border">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm rounded-lg border border-border text-text-secondary hover:text-text hover:bg-surface-hover transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => onSave(customPrompt.trim() || null)}
              disabled={saving}
              className="bg-primary text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-primary-hover transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PipelineView({ org }: { org: PipelineOrganization }) {
  const updateMutation = useUpdateStageAgents();
  const [addAgentStageId, setAddAgentStageId] = useState<string | null>(null);
  const [editingAgent, setEditingAgent] = useState<{ stageId: string; agent: StageAgentConfig } | null>(null);

  if (!org.template) {
    return (
      <div className="bg-surface rounded-xl border border-border p-8 text-center">
        <p className="text-text-muted">No active funnel template for this organization.</p>
      </div>
    );
  }

  function handleToggle(stageId: string, agentType: string, enabled: boolean) {
    const stage = org.template!.stages.find((s) => s.id === stageId);
    if (!stage) return;

    const existingAgent = stage.agents.find((a) => a.agentType === agentType);
    const agents = [
      {
        agentType,
        isEnabled: enabled,
        customPrompt: existingAgent?.customPrompt ?? undefined,
      },
    ];

    updateMutation.mutate({ orgId: org.id, stageId, agents });
  }

  function handleAddAgent(stageId: string, agentType: string) {
    updateMutation.mutate({
      orgId: org.id,
      stageId,
      agents: [{ agentType, isEnabled: true }],
    });
  }

  function handleSavePrompt(customPrompt: string | null) {
    if (!editingAgent) return;
    updateMutation.mutate(
      {
        orgId: org.id,
        stageId: editingAgent.stageId,
        agents: [
          {
            agentType: editingAgent.agent.agentType,
            isEnabled: editingAgent.agent.isEnabled,
            customPrompt: customPrompt ?? undefined,
          },
        ],
      },
      { onSuccess: () => setEditingAgent(null) },
    );
  }

  const addStage = addAgentStageId
    ? org.template.stages.find((s) => s.id === addAgentStageId)
    : null;

  const editStageName = editingAgent
    ? org.template.stages.find((s) => s.id === editingAgent.stageId)?.name ?? ''
    : '';

  return (
    <div className="min-w-0">
      <div className="flex items-center gap-3 mb-4">
        <Badge variant="primary">{org.template.name}</Badge>
        <span className="text-xs text-text-muted">
          {org.template.stages.length} stages
        </span>
      </div>

      <div className="flex gap-3 overflow-x-auto pb-4">
        {org.template.stages.map((stage, i) => (
          <div key={stage.id} className="flex items-start gap-0">
            <StageColumn
              stage={stage}
              orgId={org.id}
              onToggleAgent={handleToggle}
              onEditAgent={(stageId, agent) => setEditingAgent({ stageId, agent })}
              onAddAgent={setAddAgentStageId}
              saving={updateMutation.isPending}
            />
            {i < org.template!.stages.length - 1 && (
              <div className="flex items-center px-1 pt-12 flex-shrink-0">
                <ChevronRight size={16} className="text-text-muted" />
              </div>
            )}
          </div>
        ))}
      </div>

      {addStage && (
        <AddAgentModal
          stage={addStage}
          onAdd={(agentType) => handleAddAgent(addAgentStageId!, agentType)}
          onClose={() => setAddAgentStageId(null)}
        />
      )}

      {editingAgent && (
        <EditAgentModal
          agent={editingAgent.agent}
          stageName={editStageName}
          onSave={handleSavePrompt}
          onClose={() => setEditingAgent(null)}
          saving={updateMutation.isPending}
        />
      )}
    </div>
  );
}

type SortKey = 'leads' | 'members' | 'agents';

const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: 'leads', label: 'Leads' },
  { key: 'members', label: 'Members' },
  { key: 'agents', label: 'Agents' },
];

function sortValue(org: PipelineOrganization, key: SortKey) {
  if (key === 'members') return org.activeMembers;
  if (key === 'agents') return org.activeAgents;
  return org.totalLeads;
}

export function PipelineAgents() {
  const [selectedOrgId] = useState<string>('');
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('leads');
  const { data: organizations, isLoading } = usePipelineAgents(selectedOrgId || undefined);

  const filtered = organizations
    ?.filter((org) => !search || org.name.toLowerCase().includes(search.toLowerCase()))
    .slice()
    .sort((a, b) => sortValue(b, sortKey) - sortValue(a, sortKey));

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold">Pipeline Agents</h1>
          <p className="text-sm text-text-muted mt-1">
            Assign and configure AI agents for each pipeline stage
          </p>
        </div>
        <div className="space-y-4">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="bg-surface rounded-xl border border-border p-6 h-48 animate-pulse">
              <div className="h-4 bg-border rounded w-60" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Pipeline Agents</h1>
        <p className="text-sm text-text-muted mt-1">
          Ranked by engagement so you can spot the best clients and prioritize support
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <input
            type="text"
            placeholder="Search organizations..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-surface border border-border rounded-lg pl-9 pr-3 py-2 text-sm text-text focus:outline-none focus:border-primary"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-text-muted">Rank by</span>
          <div className="flex items-center rounded-lg border border-border bg-surface p-0.5">
            {SORT_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => setSortKey(opt.key)}
                aria-pressed={sortKey === opt.key}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  sortKey === opt.key
                    ? 'bg-primary text-white'
                    : 'text-text-secondary hover:text-text hover:bg-surface-hover'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {!filtered || filtered.length === 0 ? (
        <div className="bg-surface rounded-xl border border-border p-12 text-center">
          <Bot size={40} className="mx-auto text-text-muted mb-3" />
          <p className="text-text-muted">No organizations found.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {filtered.map((org) => (
            <OrgSection key={org.id} org={org} />
          ))}
        </div>
      )}
    </div>
  );
}

function OrgSection({ org }: { org: PipelineOrganization }) {
  const [expanded, setExpanded] = useState(true);

  return (
    <div className="bg-surface rounded-xl border border-border min-w-0">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 px-5 py-4 rounded-t-xl hover:bg-surface-hover transition-colors text-left"
      >
        {expanded ? (
          <ChevronDown size={18} className="text-text-muted flex-shrink-0" />
        ) : (
          <ChevronRight size={18} className="text-text-muted flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <h2 className="text-base font-semibold text-text">{org.name}</h2>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="default">
            <Users size={12} className="mr-1" />
            {org.activeMembers} members
          </Badge>
          <Badge variant={org.activeAgents > 0 ? 'success' : 'warning'}>
            <Power size={12} className="mr-1" />
            {org.activeAgents} active agents
          </Badge>
          <Badge variant={org.totalLeads > 0 ? 'primary' : 'default'}>
            <TrendingUp size={12} className="mr-1" />
            {org.totalLeads} leads
          </Badge>
          {!org.template && <Badge variant="warning">No pipeline</Badge>}
        </div>
      </button>

      {expanded && (
        <div className="px-5 pb-5 border-t border-border pt-4 min-w-0 overflow-hidden">
          <PipelineView org={org} />
        </div>
      )}
    </div>
  );
}
